import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'

/**
 * `/check-email` — S-D2 screen 07.
 *
 * A different page from `/verify-email`, which is the *token* page an emailed
 * link lands on. This is the one the person who just signed up is looking at
 * while they go and find the message, and the distinction matters because the
 * two have opposite jobs: one redeems a token, this one says a token is on its
 * way and offers to send another.
 *
 * **The account works without it**, and the screen says so, because S-D2 says
 * so: "You can explore while you wait, but publishing and outreach stay locked
 * until your email is confirmed." Blocking the product on a mail round trip is
 * how a trial is lost to a spam filter.
 *
 * *Resend* is `POST /auth/email/resend`, which is **authenticated** — the
 * unauthenticated form would be an oracle for which addresses have accounts, for
 * anyone willing to watch their own mail server. Signup leaves a session, so
 * this screen always has one.
 */
export function CheckEmail(): JSX.Element {
  const navigate = useNavigate()
  const { user, status, refresh } = useAuth()
  const [sentAgain, setSentAgain] = useState(false)

  const resend = useMutation(() => call(() => api.POST('/api/v1/auth/email/resend', {})))

  async function onResend(): Promise<void> {
    const result = await resend.run(undefined)
    setSentAgain(result.ok)
  }

  async function onContinue(): Promise<void> {
    // Re-read first: the address may have been confirmed in another tab, and
    // carrying on with a stale "unverified" would show the wrong banner.
    await refresh()
    void navigate(DEFAULT_HOME, { replace: true })
  }

  if (status === 'anonymous') {
    return (
      <PublicPage title="Check your inbox">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            Open the confirmation link we emailed you. If you need a new one, sign in first.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/login">
            Sign in
          </Link>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage
      title="Check your inbox"
      footer="Publishing and outreach stay locked until your address is confirmed. Everything else works now."
    >
      <div className="ar-card__body ar-stack">
        <InlineError failure={resend.state.failure} />
        {sentAgain ? (
          <div className="ar-alert ar-alert--success" role="status">
            <span className="ar-alert__icon" aria-hidden="true">
              ✓
            </span>
            <div className="ar-alert__body">
              <strong>A new link is on its way</strong>
              The previous link stopped working the moment this one was issued.
            </div>
          </div>
        ) : null}

        <div className="ar-empty">
          <div className="ar-empty__icon" aria-hidden="true">
            ✉
          </div>
          <p className="ar-empty__title">Confirm your email address</p>
          <p className="ar-empty__body">
            We sent a link to <strong>{user?.email ?? 'your address'}</strong>. It works once and
            lasts 24 hours.
          </p>
        </div>

        <p className="ar-muted" style={{ margin: 0 }}>
          Nothing after a few minutes? Check spam, and confirm the address above is the one you
          meant.
        </p>
      </div>
      <div className="ar-card__footer">
        <Button
          busy={resend.pending}
          busyLabel="Sending"
          onClick={() => {
            void onResend()
          }}
        >
          Resend email
        </Button>
        <span className="ar-card__spacer" />
        <Button
          tone="primary"
          onClick={() => {
            void onContinue()
          }}
        >
          Continue to AugRank
        </Button>
      </div>
    </PublicPage>
  )
}
